import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SimpleAvatarUpload from '@/components/common/SimpleAvatarUpload';
import { usePractitionerProfileManager } from '@/hooks/usePractitionerProfileManager';

interface AvatarUploadSectionProps {
  userId: string;
  profile: any;
  onUpdate: () => void;
}

const AvatarUploadSection: React.FC<AvatarUploadSectionProps> = ({
  userId,
  profile,
  onUpdate
}) => {
  const { t } = useTranslation('dashboard');
  const {
    isSaving,
    avatarFile,
    avatarPreview,
    handleAvatarChange,
    updateAvatar,
    getProcessedAvatarUrl
  } = usePractitionerProfileManager(userId, profile);


  const handleSaveAvatar = async (): Promise<boolean> => {
    const success = await updateAvatar();
    if (success) {
      onUpdate(); // Refresh the parent component
    }
    return success;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('profileManagement.profilePhoto')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-6">
          <div className="flex flex-col items-center gap-4">
            <SimpleAvatarUpload
              avatarUrl={getProcessedAvatarUrl()}
              avatarPreview={avatarPreview}
              userId={userId}
              onAvatarChange={handleAvatarChange}
              isUploading={isSaving}
              fallbackText={profile?.first_name?.charAt(0) || profile?.last_name?.charAt(0) || 'U'}
            />
          </div>
          <div className="flex-1">
            <p className="text-sm text-muted-foreground mb-3">
              {t('profileManagement.photoDescription')}
            </p>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• {t('profileManagement.uploadInstructions')}</p>
              <p>• {t('profileManagement.aspectRatioInfo')}</p>
              <p>• {t('profileManagement.maxFileSize')}</p>
              <p>• {t('profileManagement.supportedFormats')}</p>
            </div>
            {avatarFile && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium text-success mb-2">
                  {t('profileManagement.newPhotoSelected', { filename: avatarFile.name })}
                </p>
                <Button 
                  onClick={handleSaveAvatar}
                  disabled={isSaving}
                  size="sm"
                >
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isSaving ? t('profileManagement.uploading') : t('profileManagement.saveNewPhoto')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AvatarUploadSection;